import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Info, X, Calculator, Building2, Ruler, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAppContext } from "@/lib/onboardingContext";
import { getCostCalcSizes } from "@/lib/areaUnit";
import { getCurrencySymbol, convertFromUSD } from "@/lib/currency";

type SpaceSize = "500-1000" | "1000-2000" | "2000-4000";
type SpaceType = "second_gen" | "new_buildout";

interface CityData {
  label: string;
  rentPerSqftSecondGen: number;
  rentPerSqftNew: number;
  businessTaxReceipt: number;
  foodServiceLicense: number;
  llcFormation: number;
  legalFees: number;
  designPerSqftSecondGen: number;
  designPerSqftNew: number;
  insurance: number;
  marketingPreOpening: number;
  payrollWeeks: number;
  monthlyLaborEstimate: number;
  contingencyPct: number;
}

const CITY_DATA: Record<string, CityData> = {
  miami: {
    label: "Miami, FL",
    rentPerSqftSecondGen: 55,
    rentPerSqftNew: 72,
    businessTaxReceipt: 50,
    foodServiceLicense: 500,
    llcFormation: 500,
    legalFees: 5000,
    designPerSqftSecondGen: 120,
    designPerSqftNew: 280,
    insurance: 8000,
    marketingPreOpening: 15000,
    payrollWeeks: 6,
    monthlyLaborEstimate: 32000,
    contingencyPct: 18,
  },
};

// SPACE_SIZES now computed dynamically

const EQUIPMENT_BY_SIZE: Record<SpaceSize, number> = {
  "500-1000": 65000,
  "1000-2000": 110000,
  "2000-4000": 165000,
};

// fmt is now defined inside the component to access the city

function colorClass(amount: number) {
  if (amount < 5000) return "text-primary";
  if (amount <= 50000) return "text-accent";
  return "text-destructive";
}

function colorBg(amount: number) {
  if (amount < 5000) return "bg-primary/10";
  if (amount <= 50000) return "bg-accent/10";
  return "bg-destructive/10";
}

interface LineItem {
  name: string;
  amount: number;
  formula: string;
  source: string;
}

function mapOnboardingToSpaceSize(sqftMin: number, sqftMax: number): SpaceSize {
  const mid = (sqftMin + sqftMax) / 2;
  if (mid > 0 && mid <= 1000) return "500-1000";
  if (mid > 1000 && mid <= 2000) return "1000-2000";
  if (mid > 2000) return "2000-4000";
  return "1000-2000";
}

export default function CostCalculator() {
  const navigate = useNavigate();
  const { onboarding } = useAppContext();

  // Pre-populate from onboarding
  const [city] = useState("miami");
  const [spaceSize, setSpaceSize] = useState<SpaceSize>(
    onboarding.sqftMin > 0 || onboarding.sqftMax > 0
      ? mapOnboardingToSpaceSize(onboarding.sqftMin, onboarding.sqftMax)
      : "1000-2000"
  );
  const [spaceType, setSpaceType] = useState<SpaceType>(
    onboarding.spacePreference === "new-buildout" ? "new_buildout" : "second_gen"
  );
  const [openInfo, setOpenInfo] = useState<string | null>(null);

  const userCity = onboarding.city || "miami";
  const cs = getCurrencySymbol(userCity);
  const fmt = (n: number) => {
    const local = convertFromUSD(n, userCity);
    return cs + Math.round(local).toLocaleString();
  };

  const data = CITY_DATA[city];
  const SPACE_SIZES = getCostCalcSizes(userCity);
  const sizeObj = SPACE_SIZES.find((s) => s.value === spaceSize)!;
  const sqft = sizeObj.midpoint;

  const lineItems = useMemo<LineItem[]>(() => {
    const rentPerSqft = spaceType === "second_gen" ? data.rentPerSqftSecondGen : data.rentPerSqftNew;
    const monthlyRent = (rentPerSqft * sqft) / 12;
    const depositsRent = monthlyRent * 3;

    const licensesLegal = data.businessTaxReceipt + data.foodServiceLicense + data.llcFormation + data.legalFees;

    const designPerSqft = spaceType === "second_gen" ? data.designPerSqftSecondGen : data.designPerSqftNew;
    const designBuildout = designPerSqft * sqft;

    const equipment = EQUIPMENT_BY_SIZE[spaceSize];

    const weeklyLabor = data.monthlyLaborEstimate / 4;
    const payroll = weeklyLabor * data.payrollWeeks;

    const subtotal = depositsRent + licensesLegal + designBuildout + equipment + data.insurance + data.marketingPreOpening + payroll;
    const contingency = subtotal * (data.contingencyPct / 100);

    return [
      {
        name: "Deposits & Rent",
        amount: depositsRent,
        formula: `3 months × (${fmt(rentPerSqft)}/sqft/yr × ${sqft.toLocaleString()} sqft ÷ 12)`,
        source: "CBRE Miami Retail Market Report 2024 — average NNN asking rent for retail space",
      },
      {
        name: "Licenses & Legal",
        amount: licensesLegal,
        formula: `Business Tax Receipt ${fmt(data.businessTaxReceipt)} + Food Service License ${fmt(data.foodServiceLicense)} + LLC ${fmt(data.llcFormation)} + Legal ${fmt(data.legalFees)}`,
        source: "Miami-Dade County fee schedule; Florida DBPR licensing portal; Sunbiz.org LLC filing",
      },
      {
        name: "Design & Buildout",
        amount: designBuildout,
        formula: `${fmt(designPerSqft)}/sqft × ${sqft.toLocaleString()} sqft (${spaceType === "second_gen" ? "2nd gen" : "new buildout"})`,
        source: "RSMeans Construction Cost Data 2024; South Florida contractor estimates",
      },
      {
        name: "Equipment",
        amount: equipment,
        formula: `Fixed estimate for ${sizeObj.label} space (kitchen line, refrigeration, smallwares)`,
        source: "WebstaurantStore pricing aggregation; National Restaurant Association average startup data",
      },
      {
        name: "Insurance (Year 1)",
        amount: data.insurance,
        formula: `Flat ${fmt(data.insurance)} for Miami (GL + property + workers' comp)`,
        source: "Next Insurance & Hiscox small restaurant quotes, Miami-Dade market average",
      },
      {
        name: "Marketing Pre-Opening",
        amount: data.marketingPreOpening,
        formula: `Flat ${fmt(data.marketingPreOpening)} (branding, signage, social, soft opening)`,
        source: "Industry benchmark — National Restaurant Association startup guide",
      },
      {
        name: "Payroll Pre-Opening",
        amount: payroll,
        formula: `${data.payrollWeeks} weeks × (${fmt(data.monthlyLaborEstimate)}/mo ÷ 4)`,
        source: "BLS Occupational Employment data for Miami-Fort Lauderdale metro area",
      },
      {
        name: "Contingency (18%)",
        amount: contingency,
        formula: `${data.contingencyPct}% × subtotal ${fmt(subtotal)}`,
        source: "SBA recommended contingency buffer for food service startups",
      },
    ];
  }, [city, spaceSize, spaceType, sqft, data, sizeObj]);

  const total = lineItems.reduce((s, i) => s + i.amount, 0);
  const rangeLow = Math.round(total * 0.85);
  const rangeHigh = Math.round(total * 1.2);

  return (
    <div className="min-h-screen bg-background pb-16">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary" />
            <div>
              <h1 className="font-heading text-lg font-medium text-foreground">Startup Cost Calculator</h1>
              <p className="text-sm text-muted-foreground">Real-time estimates based on verified market data</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container pt-6 max-w-2xl">
        {/* Inputs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8"
        >
          {/* City */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> City
            </label>
            <Select value={city} disabled>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="miami">Miami, FL</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Space Size */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <Ruler className="w-3.5 h-3.5" /> Space Size
            </label>
            <Select value={spaceSize} onValueChange={(v) => setSpaceSize(v as SpaceSize)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SPACE_SIZES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Space Type */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> Space Type
            </label>
            <Select value={spaceType} onValueChange={(v) => setSpaceType(v as SpaceType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="second_gen">2nd Generation</SelectItem>
                <SelectItem value="new_buildout">New Buildout</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Line Items */}
        <div className="space-y-2">
          {lineItems.map((item, i) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.04 }}
              className="bg-card border border-border rounded-xl overflow-hidden"
            >
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${
                    item.amount < 5000 ? "bg-primary" : item.amount <= 50000 ? "bg-accent" : "bg-destructive"
                  }`} />
                  <span className="text-sm font-medium text-foreground truncate">{item.name}</span>
                  <button
                    onClick={() => setOpenInfo(openInfo === item.name ? null : item.name)}
                    className="shrink-0 p-1 rounded-md hover:bg-muted/50 transition-colors"
                    aria-label={`Info about ${item.name}`}
                  >
                    <Info className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                </div>
                <span className={`text-sm font-medium whitespace-nowrap ${colorClass(item.amount)}`}>
                  {fmt(item.amount)}
                </span>
              </div>

              <AnimatePresence>
                {openInfo === item.name && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className={`mx-4 mb-4 p-3 rounded-lg ${colorBg(item.amount)} border border-border/50`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="space-y-2 text-xs">
                          <div>
                            <span className="font-medium text-foreground">Formula: </span>
                            <span className="text-muted-foreground">{item.formula}</span>
                          </div>
                          <div>
                            <span className="font-medium text-foreground">Source: </span>
                            <span className="text-muted-foreground">{item.source}</span>
                          </div>
                        </div>
                        <button onClick={() => setOpenInfo(null)} className="shrink-0 p-0.5">
                          <X className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Total */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mt-6 bg-card border-2 border-primary/30 rounded-xl p-6 text-center"
        >
          <p className="text-sm text-muted-foreground mb-1">Estimated Total Range</p>
          <p className="font-heading text-3xl font-medium text-foreground">
            {fmt(rangeLow)} – {fmt(rangeHigh)}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Base estimate: {fmt(total)} · Range: −15% to +20%
          </p>

          {/* Color legend */}
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-primary" /> Under $5K</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-accent" /> $5K–$50K</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-destructive" /> Over $50K</span>
          </div>
        </motion.div>

        {/* Data Sources Footer */}
        <div className="mt-8 mb-4 p-4 rounded-xl bg-muted/30 border border-border">
          <p className="text-xs font-medium text-foreground mb-2">📋 Data Sources</p>
          <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
            <li>Miami-Dade County — Local Business Tax Receipt fee schedule (miamidade.gov)</li>
            <li>Florida DBPR — Division of Hotels & Restaurants license fees (myfloridalicense.com)</li>
            <li>CBRE Miami Retail Market Report Q4 2024 — Average retail asking rents</li>
            <li>RSMeans 2024 — Commercial restaurant buildout cost per sqft</li>
            <li>U.S. Bureau of Labor Statistics — Occupational wage data, Miami metro</li>
            <li>SBA.gov — Recommended contingency buffers for food service startups</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
